import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const SubjectLogin = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  // Persistent confirmation state. Once a request resolves, swap the card to a
  // neutral "check your email" panel and never re-show the form behind it — a
  // returning subject arrives via the email link, not this page.
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    // Local UX-only shape check before any network call. Stays on the form.
    if (!trimmed || !trimmed.includes("@")) {
      toast({ title: "Enter a valid email address.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // The edge fn always returns a neutral 200; `error` is set only on a
      // network/CORS/function-invocation failure, never on eligibility.
      const { error } = await supabase.functions.invoke("subject-login", {
        body: { email: trimmed.toLowerCase() },
      });
      if (error) throw error;
      // Identical confirmation regardless of outcome — never reveal whether the
      // email maps to a real subject.
      setSubmitted(true);
    } catch {
      toast({
        title:
          "Something went wrong while requesting your sign-in link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-8 text-center">
          <Link to="/">
            <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-48 mx-auto mb-4" />
          </Link>
          <div className="space-y-2">
            <h1 className="font-display text-2xl font-bold text-foreground">Check your email</h1>
            <p className="text-muted-foreground text-sm">
              If that email is eligible to manage a tribute, we have sent a sign-in
              link. Click the link in your email to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <Link to="/">
            <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-48 mx-auto mb-4" />
          </Link>
          <h1 className="font-display text-2xl font-bold text-foreground">
            Sign in to manage your tribute
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Enter your email and we will send you a secure sign-in link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Sending link..." : "Send sign-in link"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SubjectLogin;
